import { useState, useEffect, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Loader2, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Progress } from '@/components/ui/progress';
import { ToastAction } from '@/components/ui/toast';
import { useToast } from '@/hooks/use-toast';
import { useWorkspaces } from '@/hooks/useWorkspaces';
import { useAuth } from '@/contexts/AuthContext';
import { useContactOptions } from '@/hooks/useContactOptions';
import { supabase } from '@/lib/supabase';
import { captureError } from '@/lib/sentry';
import { toRpcFilters } from '@/lib/filters/rpc';
import { isUniqueViolation } from '@/lib/error-handling';
import { getInvalidationKeys } from '@/lib/query-keys';
import type { Contact } from '@/types/contact';
import type { UseBulkSelectionReturn } from '@/hooks/useBulkSelection';

const UNCHANGED = '__unchanged__';
const CLEAR_VALUE = '__clear__';

interface Group {
  id: string;
  name: string;
  contact_count: number;
}

interface BulkEditChanges {
  relationship: string;
  type: string;
  rank: string;
  status: string;
  remarket: string;
  addToGroupIds: string[];
  removeFromGroupId: string;
}

const INITIAL_CHANGES: BulkEditChanges = {
  relationship: UNCHANGED,
  type: UNCHANGED,
  rank: UNCHANGED,
  status: UNCHANGED,
  remarket: UNCHANGED,
  addToGroupIds: [],
  removeFromGroupId: '',
};

interface BulkEditDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  selectedContacts: Contact[];
  bulkSelection: UseBulkSelectionReturn;
  onSuccess: () => void;
  requireConfirmation?: boolean;
}

export function BulkEditDialog({
  open,
  onOpenChange,
  selectedContacts,
  bulkSelection,
  onSuccess,
  requireConfirmation,
}: BulkEditDialogProps) {
  const effectiveCount = bulkSelection.effectiveCount ?? selectedContacts.length;
  const isAllMatchingMode = bulkSelection.mode === 'all_matching';

  const [changes, setChanges] = useState<BulkEditChanges>(INITIAL_CHANGES);
  const [groups, setGroups] = useState<Group[]>([]);
  const [isApplying, setIsApplying] = useState(false);
  // What the apply is doing right now. total 0 = no count to show, label only.
  const [progress, setProgress] = useState<{ label: string; done: number; total: number } | null>(null);
  const step = (label: string, done = 0, total = 0) => setProgress({ label, done, total });
  const [error, setError] = useState<string | null>(null);
  const [showConfirmation, setShowConfirmation] = useState(false);

  // Group autocomplete state
  const [groupInput, setGroupInput] = useState('');
  const [showGroupSuggestions, setShowGroupSuggestions] = useState(false);
  const groupInputRef = useRef<HTMLInputElement>(null);
  const groupSuggestionsRef = useRef<HTMLDivElement>(null);

  const resolvedIdsRef = useRef<string[]>([]);

  const { toast } = useToast();
  const { currentWorkspace } = useWorkspaces();
  const { user } = useAuth();
  const { options } = useContactOptions();
  const queryClient = useQueryClient();

  // Filter group suggestions
  const filteredGroups = groups.filter(g =>
    g.name.toLowerCase().includes(groupInput.toLowerCase()) &&
    !changes.addToGroupIds.includes(g.id)
  );
  const exactMatch = groups.some(g => g.name.toLowerCase() === groupInput.trim().toLowerCase());

  const hasFieldChanges =
    changes.relationship !== UNCHANGED ||
    changes.type !== UNCHANGED ||
    changes.rank !== UNCHANGED ||
    changes.status !== UNCHANGED ||
    changes.remarket !== UNCHANGED;

  const hasGroupChanges = changes.addToGroupIds.length > 0 || changes.removeFromGroupId !== '';

  const hasChanges = hasFieldChanges || hasGroupChanges;

  // Setting ReMarket=YES grants marketing consent — gate Apply on an affirmation
  // and log it per contact. See #447 (marketing-consent audit).
  const settingRemarketYes = changes.remarket === 'YES';

  // Re-subscribe guard: bulk-setting ReMarket=YES on contacts that previously
  // unsubscribed is a re-subscribe, not a fresh opt-in. Surface a count in the
  // confirmation so the operator knows. Best-effort from the explicit
  // selection; the authoritative set is recomputed at apply time so
  // all_matching mode is covered too. Mirrors the single-contact "Re-enable
  // Marketing" flow in src/pages/contacts/[id].tsx.
  // BOTH grains, or this undercounts. The consent trigger blocks on
  // `unsubscribed IS TRUE OR remarket = 'unsubscribe'`, and bulk edit can set
  // remarket='unsubscribe' WITHOUT setting the flag — so a contact can be in the
  // drifted state (`remarket='unsubscribe', unsubscribed=false`) after two ordinary
  // clicks. Detecting on one grain while the database enforces on two means we skip
  // the doorway and then take a hard `consent_locked` failure mid-apply.
  const resubscribeCandidates = settingRemarketYes
    ? selectedContacts.filter((c) => c.unsubscribed || c.remarket === 'unsubscribe')
    : [];

  const sameGroupError =
    changes.removeFromGroupId !== '' &&
    changes.addToGroupIds.includes(changes.removeFromGroupId);

  // Reset state when dialog opens/closes
  useEffect(() => {
    if (open) {
      setChanges(INITIAL_CHANGES);
      setError(null);
      setGroupInput('');
      setShowGroupSuggestions(false);
      fetchGroups();
    }
  }, [open]);

  // Click-outside to close group suggestions
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        groupSuggestionsRef.current && !groupSuggestionsRef.current.contains(event.target as Node) &&
        groupInputRef.current && !groupInputRef.current.contains(event.target as Node)
      ) {
        setShowGroupSuggestions(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const fetchGroups = async () => {
    if (!currentWorkspace) return;

    try {
      const { data, error: fetchError } = await supabase
        .from('view_group_counts')
        .select('group_id, group_name, contact_count')
        .eq('workspace_id', currentWorkspace.id);

      if (fetchError) throw fetchError;

      setGroups(
        (data ?? []).map((g) => ({
          id: g.group_id,
          name: g.group_name,
          contact_count: g.contact_count,
        }))
      );
    } catch (err) {
      console.error('Error fetching groups:', err);
    }
  };

  const resolveContactIds = async (): Promise<string[]> => {
    if (!isAllMatchingMode || !currentWorkspace || !bulkSelection.filterState) {
      console.log('[BulkEdit] Using explicit contact IDs:', selectedContacts.length);
      return selectedContacts.map((c) => c.id);
    }
    console.log('[BulkEdit] Resolving all_matching IDs with filters:', bulkSelection.filterState);

    // Fetch in chunks of 1000 to bypass PostgREST row limit
    const allIds: string[] = [];
    let offset = 0;
    const chunkSize = 1000;
    let hasMore = true;

    while (hasMore) {
      const { data: chunk, error: rpcError } = await supabase
        .rpc('get_filtered_contact_ids', {
          p_workspace_id: currentWorkspace.id,
          p_filters: bulkSelection.filterState ? toRpcFilters(bulkSelection.filterState) : null,
        })
        .range(offset, offset + chunkSize - 1);

      if (rpcError) {
        console.error('[BulkEdit] get_filtered_contact_ids error:', rpcError);
        throw new Error(rpcError.message || 'Failed to resolve contacts');
      }

      const chunkIds = (chunk || []).map((row: { contact_id: string }) => row.contact_id);
      allIds.push(...chunkIds);

      hasMore = chunkIds.length === chunkSize;
      offset += chunkSize;
    }

    const ids = allIds.filter((id: string) => !bulkSelection.excludedIds.has(id));
    console.log('[BulkEdit] Resolved', ids.length, 'contact IDs (fetched in', Math.ceil(offset / chunkSize), 'chunks)');
    return ids;
  };

  const invalidateQueries = async () => {
    if (!currentWorkspace) return;
    const contactKeys = getInvalidationKeys.afterContactMutation(currentWorkspace.id);
    const groupKeys = getInvalidationKeys.afterGroupMutation(currentWorkspace.id);
    await Promise.all(
      [...contactKeys, ...groupKeys].map((key) =>
        queryClient.invalidateQueries({ queryKey: key })
      )
    );
  };

  const handleApply = async () => {
    if (!currentWorkspace || !hasChanges || sameGroupError) return;

    setIsApplying(true);
    setError(null);

    try {
      const CHUNK_SIZE = 200;
      // Each consent activity fires a per-row search-vector trigger that rebuilds
      // the contact's whole search vector, so a 200-row insert took ~7.8s and
      // tripped the 8s `authenticated` statement timeout (PostgREST 500). The
      // companion migration halves that (~19ms/row remains, all of it the
      // re-aggregation); halving the batch too keeps this well clear of the ceiling.
      // ponytail: batch size, not a cure — the per-row trigger is the real cost (#700).
      const CONSENT_CHUNK_SIZE = 100;
      step('Finding the contacts to update…');
      const contactIds = await resolveContactIds();
      resolvedIdsRef.current = contactIds;

      if (contactIds.length === 0) {
        setError('No contacts to update');
        return;
      }

      let operationId: string | null = null;

      // Among the contacts being set to YES, find those that previously
      // unsubscribed. Re-enabling marketing for them is a *re-subscribe*: it
      // needs its own audit line (who re-subscribed them, by email) and must
      // clear the unsubscribed flag — otherwise they sit in an inconsistent
      // unsubscribed=true + remarket=YES state that lets an unsubscribed
      // contact slip back into campaigns. Authoritative query so it's correct
      // in both explicit and all_matching selection modes.
      let resubscribeIds: string[] = [];
      if (settingRemarketYes) {
        step('Checking unsubscribe status', 0, contactIds.length);
        for (let i = 0; i < contactIds.length; i += CHUNK_SIZE) {
          const chunk = contactIds.slice(i, i + CHUNK_SIZE);
          // Must match the trigger's predicate exactly — see resubscribeCandidates
          // above. `.eq('unsubscribed', true)` alone misses the drifted state and the
          // whole chunk then dies on consent_locked.
          const { data: unsubRows, error: unsubErr } = await supabase
            .from('contacts')
            .select('id')
            .eq('workspace_id', currentWorkspace.id)
            .in('id', chunk)
            .or('unsubscribed.eq.true,remarket.eq.unsubscribe');
          if (unsubErr) {
            console.error('[BulkEdit] unsubscribe-status check failed:', unsubErr);
            captureError(unsubErr as Error, { feature: 'bulk-resubscribe-check' });
            setError('Could not verify unsubscribe status — no changes were applied.');
            return;
          }
          resubscribeIds.push(...(unsubRows ?? []).map((r) => r.id));
          step('Checking unsubscribe status', Math.min(i + CHUNK_SIZE, contactIds.length), contactIds.length);
        }
      }
      const resubscribeSet = new Set(resubscribeIds);

      // Step 0: Marketing consent — when setting ReMarket=YES, log one activity
      // per contact BEFORE applying the field change, so we never set
      // remarket=YES without an audit entry. Abort the whole apply if logging
      // fails.
      //
      // Previously-unsubscribed contacts are EXCLUDED here: they go through the
      // re-subscribe doorway in step 0b, which writes their audit row itself,
      // inside the same transaction as the consent change. Logging them here too
      // would double-audit them.
      if (settingRemarketYes) {
        const consentRows = contactIds
          .filter((cid) => !resubscribeSet.has(cid))
          .map((cid) => ({
            contact_id: cid,
            workspace_id: currentWorkspace.id,
            user_id: user?.id ?? null,
            type: 'note',
            title: 'Marketing consent confirmed',
            description:
              'Bulk edit: user affirmed they have permission to send these contacts marketing email (set ReMarket = Yes).',
            start_date: new Date().toISOString(),
            scheduled: false,
            completed: true,
          }));
        step('Recording marketing consent', 0, consentRows.length);
        for (let i = 0; i < consentRows.length; i += CONSENT_CHUNK_SIZE) {
          const { error: consentErr } = await supabase
            .from('activities')
            .insert(consentRows.slice(i, i + CONSENT_CHUNK_SIZE));
          if (consentErr) {
            console.error('[BulkEdit] consent log insert failed:', consentErr);
            captureError(consentErr as Error, { feature: 'marketing-consent-log-bulk' });
            setError('Could not record marketing consent — no changes were applied.');
            return;
          }
          // After the error check — a failed chunk must not report itself as done.
          step('Recording marketing consent', Math.min(i + CONSENT_CHUNK_SIZE, consentRows.length), consentRows.length);
        }
      }

      // Step 0b: RE-SUBSCRIBE, through the doorway, BEFORE the field update.
      //
      // Ordering is load-bearing, both ways:
      //  - The doorway is the only thing that clears email_address.email_status.
      //    Without it, re-subscribed contacts pass the campaign create gate, get
      //    queue jobs, and have every job killed at send with RECIPIENT_SUPPRESSED
      //    — silently unmailable. That was live for 5 weeks (todd + jessica).
      //  - It must run BEFORE bulk_edit_contacts, not after: once the consent
      //    triggers land, a plain UPDATE remarket='YES' on a still-unsubscribed
      //    contact raises consent_locked and takes the whole chunk with it. Run the
      //    doorway first and the contact is no longer unsubscribed by the time the
      //    field update touches it.
      //
      // FATAL on error: the Step 0 audit rows are already written, so bailing here
      // leaves an audit entry with no consent change (safe, and visible) rather
      // than a consent change with no way back (not).
      let stillUndeliverable = 0;
      if (resubscribeIds.length > 0) {
        for (let i = 0; i < resubscribeIds.length; i += CHUNK_SIZE) {
          const chunk = resubscribeIds.slice(i, i + CHUNK_SIZE);
          const { data: outcomes, error: resubErr } = await supabase.rpc(
            'hermes_resubscribe_contacts',
            {
              p_workspace_id: currentWorkspace.id,
              p_contact_ids: chunk,
              p_source: 'bulk_edit',
            }
          );
          if (resubErr) {
            console.error('[BulkEdit] re-subscribe doorway failed:', resubErr);
            captureError(resubErr as Error, { feature: 'bulk-resubscribe' });
            setError(
              'Could not re-subscribe the previously-unsubscribed contacts, so no changes were applied. Try again, and if it keeps failing, contact support.'
            );
            return;
          }
          // A bounced/complained address stays blocked even with consent — the
          // mailbox is dead, and consent does not resurrect a dead mailbox. Tell
          // the user, because the remedy is a different address, not a retry.
          //
          // PRIMARY only: campaigns only ever mail the primary address, so warning
          // on a stale bounced *alternate* row would be a false alarm about someone
          // we can mail perfectly well.
          stillUndeliverable += (outcomes ?? []).filter(
            (o: { outcome: string; is_primary: boolean }) =>
              o.outcome === 'still_undeliverable' && o.is_primary
          ).length;
        }
      }

      // Step 1: Apply field changes
      let remarketSkipped = 0;
      if (hasFieldChanges) {
        const updates: Record<string, string> = {};
        for (const { field } of fieldRows) {
          if (changes[field] !== UNCHANGED) {
            updates[field] = changes[field] === CLEAR_VALUE ? '' : changes[field];
          }
        }

        // A contact who withdrew consent has their ReMarket status LOCKED at the database
        // level — not just against 'YES', but against any move away from 'unsubscribe'.
        // Only the doorway may change it.
        //
        // So when ReMarket is being set to something OTHER than YES (e.g. 'NO'), leave
        // those contacts' remarket alone and still apply the rest of the edit to them.
        // Sending remarket for them would raise consent_locked, kill the chunk, and leave
        // the EARLIER chunks already committed — a partial apply reported as a generic
        // failure, with an error telling the user to re-subscribe someone in order to mark
        // them "do not market". (Setting YES is different: those contacts went through the
        // doorway in step 0b, so by now they are no longer locked.)
        const lockedIds = new Set<string>(
          updates.remarket !== undefined && updates.remarket !== 'YES'
            ? await (async () => {
                step('Checking unsubscribe status', 0, contactIds.length);
                const locked: string[] = [];
                for (let i = 0; i < contactIds.length; i += CHUNK_SIZE) {
                  const { data, error } = await supabase
                    .from('contacts')
                    .select('id')
                    .eq('workspace_id', currentWorkspace.id)
                    .in('id', contactIds.slice(i, i + CHUNK_SIZE))
                    .or('unsubscribed.eq.true,remarket.eq.unsubscribe');
                  if (error) throw error;
                  locked.push(...(data ?? []).map((r) => r.id as string));
                  step(
                    'Checking unsubscribe status',
                    Math.min(i + CHUNK_SIZE, contactIds.length),
                    contactIds.length
                  );
                }
                return locked;
              })()
            : []
        );
        remarketSkipped = lockedIds.size;

        const updatesWithoutRemarket = { ...updates };
        delete updatesWithoutRemarket.remarket;
        const hasOtherFields = Object.keys(updatesWithoutRemarket).length > 0;

        let appliedSoFar = 0;
        const apply = async (
          ids: string[],
          payload: Record<string, string>,
          captureOperationId: boolean
        ) => {
          for (let i = 0; i < ids.length; i += CHUNK_SIZE) {
            const chunk = ids.slice(i, i + CHUNK_SIZE);
            const { data, error: editError } = await supabase.rpc('bulk_edit_contacts', {
              p_workspace_id: currentWorkspace.id,
              p_contact_ids: chunk,
              p_updates: payload,
            });
            if (editError) throw editError;
            if (captureOperationId && i === 0) {
              operationId = data?.operation_id ?? null;
            }
            appliedSoFar += chunk.length;
            step('Updating contacts', appliedSoFar, contactIds.length);
          }
        };

        step('Updating contacts', 0, contactIds.length);
        const editableIds = contactIds.filter((id) => !lockedIds.has(id));
        await apply(editableIds, updates, true);
        if (lockedIds.size > 0 && hasOtherFields) {
          await apply([...lockedIds], updatesWithoutRemarket, editableIds.length === 0);
        }
      }

      // (The old step 1b — a best-effort `unsubscribed = false` after the field
      // update — is gone. It cleared only the permission grain, left
      // email_address.email_status suppressed, and did it AFTER the field update.
      // Step 0b replaces it: both grains, transactionally audited, before.)

      // Step 2: Apply group operations
      const groupOps: PromiseLike<unknown>[] = [];

      if (changes.addToGroupIds.length > 0) {
        for (const groupId of changes.addToGroupIds) {
          // Chunk contact IDs into batches of 200
          for (let i = 0; i < contactIds.length; i += CHUNK_SIZE) {
            const chunk = contactIds.slice(i, i + CHUNK_SIZE);
            groupOps.push(
              supabase.rpc('bulk_add_to_group', {
                p_contact_ids: chunk,
                p_workspace_id: currentWorkspace.id,
                p_group_id: groupId,
              }).then(({ data: result, error: e }) => {
                if (e) {
                  console.error('[BulkEdit] bulk_add_to_group error:', e.message, e.details, e.hint);
                  throw e;
                }
                if (result && !result.success) {
                  console.error('[BulkEdit] bulk_add_to_group failed:', result.error);
                  throw new Error(result.error || 'Failed to add to group');
                }
              })
            );
          }
        }
      }

      if (changes.removeFromGroupId) {
        for (let i = 0; i < contactIds.length; i += CHUNK_SIZE) {
          const chunk = contactIds.slice(i, i + CHUNK_SIZE);
          groupOps.push(
            supabase.rpc('bulk_remove_from_group', {
              p_contact_ids: chunk,
              p_workspace_id: currentWorkspace.id,
              p_group_id: changes.removeFromGroupId,
            }).then(({ error: e }) => { if (e) throw e; })
          );
        }
      }

      if (groupOps.length > 0) {
        // ponytail: label only, no counter — these run concurrently and finish fast.
        step('Updating groups…');
        await Promise.all(groupOps);
      }

      // Success: close dialog, invalidate, show undo toast
      step('Refreshing your contacts…');
      onOpenChange(false);
      await invalidateQueries();
      onSuccess();

      // Capture values for undo closure
      const undoOperationId = operationId;
      const undoContactIds = contactIds;
      const undoAddGroupIds = [...changes.addToGroupIds];
      const undoRemoveGroupId = changes.removeFromGroupId;
      const undoWorkspaceId = currentWorkspace.id;

      // Report the dead-mailbox outcome. Consent is reversible; a bounced or
      // spam-reported address is not, and it stays blocked. Saying nothing here is
      // what made re-subscribed contacts *silently* unmailable in the first place.
      const undeliverableNote = stillUndeliverable > 0
        ? ` — but ${stillUndeliverable.toLocaleString()} still can't be emailed: their address previously bounced or was reported as spam, so it stays blocked to protect your sending reputation. Add a different email address for them.`
        : '';

      // Say what we deliberately did NOT do. A silent skip is its own bug.
      const remarketSkippedNote = remarketSkipped > 0
        ? ` ReMarket was left unchanged for ${remarketSkipped.toLocaleString()} contact${remarketSkipped !== 1 ? 's' : ''} who previously unsubscribed — their status is locked because they opted out. Every other change was applied to them.`
        : '';

      toast({
        description:
          `Updated ${contactIds.length.toLocaleString()} contact${contactIds.length !== 1 ? 's' : ''}` +
          undeliverableNote +
          remarketSkippedNote,
        duration: stillUndeliverable > 0 || remarketSkipped > 0 ? 30000 : 15000,
        action: (
          <ToastAction
            altText="Undo bulk edit"
            onClick={async () => {
              try {
                const undoOps: PromiseLike<unknown>[] = [];

                // Undo field changes
                if (undoOperationId) {
                  undoOps.push(
                    supabase.rpc('bulk_undo_edit', {
                      p_workspace_id: undoWorkspaceId,
                      p_operation_id: undoOperationId,
                    }).then(({ error: e }) => { if (e) throw e; })
                  );
                }

                // Reverse group additions (remove what was added)
                for (const gid of undoAddGroupIds) {
                  undoOps.push(
                    supabase.rpc('bulk_remove_from_group', {
                      p_contact_ids: undoContactIds,
                      p_workspace_id: undoWorkspaceId,
                      p_group_id: gid,
                    }).then(({ error: e }) => { if (e) throw e; })
                  );
                }

                // Reverse group removals (re-add what was removed)
                if (undoRemoveGroupId) {
                  undoOps.push(
                    supabase.rpc('bulk_add_to_group', {
                      p_contact_ids: undoContactIds,
                      p_workspace_id: undoWorkspaceId,
                      p_group_id: undoRemoveGroupId,
                    }).then(({ error: e }) => { if (e) throw e; })
                  );
                }

                await Promise.all(undoOps);

                // Invalidate after undo
                const contactKeys = getInvalidationKeys.afterContactMutation(undoWorkspaceId);
                const groupKeys = getInvalidationKeys.afterGroupMutation(undoWorkspaceId);
                await Promise.all(
                  [...contactKeys, ...groupKeys].map((key) =>
                    queryClient.invalidateQueries({ queryKey: key })
                  )
                );

                onSuccess();
                toast({ description: 'Bulk edit undone' });
              } catch (undoErr) {
                console.error('Error undoing bulk edit:', undoErr);
                captureError(undoErr as Error, { feature: 'contacts-bulk-edit-save' });

                // `bulk_undo_edit` restores remarket from a snapshot. If a contact in
                // that set has unsubscribed since the edit, the consent trigger
                // refuses to put their old value back — correctly: an undo must not
                // resurrect consent a person has withdrawn. Say that, rather than
                // showing them a raw Postgres error.
                const isConsentLocked = String(
                  (undoErr as { message?: string })?.message ?? undoErr
                ).includes('consent_locked');

                toast({
                  title: isConsentLocked ? 'Undo stopped' : 'Error',
                  description: isConsentLocked
                    ? "Undo can't restore marketing consent for a contact who has since unsubscribed. Nothing was changed. If they have asked to receive marketing email again, re-subscribe them from their contact record."
                    : 'Failed to undo changes',
                  variant: 'destructive',
                });
              }
            }}
          >
            Undo
          </ToastAction>
        ),
      });
    } catch (err) {
      const errMsg = err instanceof Error ? err.message : typeof err === 'object' && err !== null ? JSON.stringify(err) : String(err);
      console.error('[BulkEdit] Error applying bulk edit:', errMsg, err);
      captureError(err as Error, { feature: 'bulk-edit' });
      setError(errMsg || 'Failed to apply changes. Please try again.');
    } finally {
      setIsApplying(false);
      setProgress(null);
    }
  };

  const selectExistingGroup = (groupId: string) => {
    setChanges(prev => ({
      ...prev,
      addToGroupIds: [...prev.addToGroupIds, groupId],
    }));
    setGroupInput('');
    setShowGroupSuggestions(false);
  };

  const removeAddGroup = (groupId: string) => {
    setChanges(prev => ({
      ...prev,
      addToGroupIds: prev.addToGroupIds.filter(id => id !== groupId),
    }));
  };

  const handleAddGroup = async (name: string) => {
    const trimmed = name.trim();
    if (!trimmed || !currentWorkspace) return;

    // Check if group already exists
    const existing = groups.find(g => g.name.toLowerCase() === trimmed.toLowerCase());
    if (existing) {
      if (!changes.addToGroupIds.includes(existing.id)) {
        selectExistingGroup(existing.id);
      }
      return;
    }

    // Create new group
    try {
      const { data: newGroup, error: createErr } = await supabase
        .from('groups')
        .insert({ workspace_id: currentWorkspace.id, group_name: trimmed, created_by: user?.id || null })
        .select()
        .single();

      if (createErr) throw createErr;

      // Add to local groups list and select it
      setGroups(prev => [...prev, { id: newGroup.id, name: newGroup.group_name, contact_count: 0 }]);
      selectExistingGroup(newGroup.id);
    } catch (err) {
      console.error('Error creating group:', err);
      if (isUniqueViolation(err)) {
        toast({
          title: 'Group already exists',
          description: `A group named "${trimmed}" already exists in this workspace.`,
          variant: 'destructive',
        });
        return;
      }
      captureError(err as Error, { feature: 'create-group' });
      toast({ description: 'Failed to create group', variant: 'destructive' });
    }
  };

  type StringField = 'relationship' | 'type' | 'rank' | 'status' | 'remarket' | 'removeFromGroupId';

  const updateField = (field: StringField, value: string) => {
    setChanges((prev) => ({ ...prev, [field]: value }));
  };

  const fieldRows: { label: string; field: StringField; options: string[] }[] = [
    { label: 'Relationship', field: 'relationship', options: options.relationship },
    { label: 'Type', field: 'type', options: options.type },
    { label: 'Rank', field: 'rank', options: options.rank },
    { label: 'Status', field: 'status', options: options.status },
    // System canonical options for remarket — writing YES/NO normalizes the
    // 6 raw value patterns currently in prod. 'unsubscribe' is the value the
    // unsubscribe flow writes (see email-unsubscribe edge function); exposing
    // it here lets ops filter/segment unsubscribed contacts. "Clear" (via
    // existing CLEAR_VALUE sentinel) writes empty string.
    { label: 'Remarket', field: 'remarket', options: ['YES', 'NO', 'unsubscribe'] },
  ];

  return (
    <>
    <Dialog open={open} onOpenChange={isApplying ? undefined : onOpenChange}>
      <DialogContent className="sm:max-w-xl">
        <DialogHeader>
          <DialogTitle>
            Bulk Edit{' '}
            <span className="font-normal text-muted-foreground">
              &middot; {effectiveCount.toLocaleString()} contact{effectiveCount !== 1 ? 's' : ''}
            </span>
          </DialogTitle>
          <DialogDescription className="text-sm text-muted-foreground">
            Only changed fields will be updated.
            {isAllMatchingMode && (
              <span className="block text-xs mt-1">(All matching results selected)</span>
            )}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          {/* Smart field selects */}
          <div className="space-y-3">
            {fieldRows.map(({ label, field, options: fieldOptions }) => (
              <div key={field} className="flex items-center gap-4">
                <span className="w-32 text-sm text-right text-muted-foreground shrink-0">
                  {label}
                </span>
                <Select
                  value={changes[field]}
                  onValueChange={(value) => updateField(field, value)}
                >
                  <SelectTrigger className={`flex-1 ${changes[field] === CLEAR_VALUE ? 'text-muted-foreground italic' : ''}`}>
                    <SelectValue placeholder="-- unchanged --" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={UNCHANGED}>
                      -- unchanged --
                    </SelectItem>
                    <SelectItem value={CLEAR_VALUE} className="text-muted-foreground italic">
                      Clear value
                    </SelectItem>
                    {fieldOptions.map((opt) => (
                      <SelectItem key={opt} value={opt}>
                        {opt}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            ))}
          </div>

          {/* Groups separator */}
          <div className="relative py-2">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t" />
            </div>
            <div className="relative flex justify-center text-xs uppercase">
              <span className="bg-background px-2 text-muted-foreground">Groups</span>
            </div>
          </div>

          {/* Group selects */}
          <div className="space-y-3">
            <div className="flex items-start gap-4">
              <span className="w-32 text-sm text-right text-muted-foreground shrink-0 pt-2">
                Add to Group
              </span>
              <div className="flex-1 space-y-2">
                {/* Selected group badges */}
                {changes.addToGroupIds.length > 0 && (
                  <div className="flex flex-wrap gap-1">
                    {changes.addToGroupIds.map(id => {
                      const group = groups.find(g => g.id === id);
                      return (
                        <Badge key={id} variant="secondary" className="text-xs gap-1">
                          {group?.name || id}
                          <button
                            type="button"
                            onClick={() => removeAddGroup(id)}
                            className="ml-1 hover:text-destructive"
                          >
                            <X className="h-3 w-3" />
                          </button>
                        </Badge>
                      );
                    })}
                  </div>
                )}

                {/* Autocomplete input */}
                <div className="relative">
                  <Input
                    ref={groupInputRef}
                    value={groupInput}
                    onChange={(e) => { setGroupInput(e.target.value); setShowGroupSuggestions(true); }}
                    onFocus={() => groupInput && setShowGroupSuggestions(true)}
                    onKeyDown={async (e) => {
                      if (e.key === 'Enter' && groupInput.trim()) {
                        e.preventDefault();
                        e.stopPropagation();
                        await handleAddGroup(groupInput.trim());
                      } else if (e.key === 'Escape') {
                        setShowGroupSuggestions(false);
                      }
                    }}
                    placeholder="Type to search or create a group..."
                    className="h-8 text-sm"
                    autoComplete="off"
                  />

                  {/* Suggestions dropdown */}
                  {showGroupSuggestions && groupInput.trim() && (
                    <div
                      ref={groupSuggestionsRef}
                      className="absolute z-50 w-full mt-1 bg-popover border rounded-md shadow-md max-h-[200px] overflow-y-auto"
                    >
                      {filteredGroups.map(group => (
                        <div
                          key={group.id}
                          className="px-3 py-2 text-sm cursor-pointer hover:bg-accent"
                          onMouseDown={(e) => {
                            e.preventDefault();
                            selectExistingGroup(group.id);
                          }}
                        >
                          {group.name} <span className="text-muted-foreground">({group.contact_count})</span>
                        </div>
                      ))}
                      {!exactMatch && groupInput.trim() && (
                        <div
                          className="px-3 py-2 text-sm cursor-pointer hover:bg-accent text-primary"
                          onMouseDown={(e) => {
                            e.preventDefault();
                            handleAddGroup(groupInput.trim());
                          }}
                        >
                          + Create &ldquo;{groupInput.trim()}&rdquo;
                        </div>
                      )}
                      {filteredGroups.length === 0 && exactMatch && (
                        <div className="px-3 py-2 text-sm text-muted-foreground">Already added</div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="flex items-center gap-4">
              <span className="w-32 text-sm text-right text-muted-foreground shrink-0">
                Remove from Group
              </span>
              <Select
                value={changes.removeFromGroupId || undefined}
                onValueChange={(value) => updateField('removeFromGroupId', value === '_clear_' ? '' : value)}
              >
                <SelectTrigger className="flex-1">
                  <SelectValue placeholder="Select group..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="_clear_">-- none --</SelectItem>
                  {groups.length === 0 ? (
                    <SelectItem value="_none" disabled>
                      No groups available
                    </SelectItem>
                  ) : (
                    groups.map((group) => (
                      <SelectItem
                        key={group.id}
                        value={group.id}
                        disabled={changes.addToGroupIds.includes(group.id)}
                      >
                        {group.name} ({group.contact_count})
                      </SelectItem>
                    ))
                  )}
                </SelectContent>
              </Select>
            </div>

            {sameGroupError && (
              <p className="text-sm text-destructive ml-36">
                Cannot add and remove from the same group.
              </p>
            )}
          </div>
        </div>

        {/* Inline error */}
        {error && (
          <div className="rounded-md border border-destructive/50 bg-destructive/10 p-3 text-sm text-destructive">
            {error}
          </div>
        )}

        {isApplying && progress && (
          <div className="space-y-1.5 pt-2" role="status">
            {/* No bar for a phase with no count (resolving, refreshing) — a bar parked
                at a made-up percentage is worse than none. */}
            {progress.total > 0 && <Progress value={(progress.done / progress.total) * 100} />}
            <p className="text-xs text-muted-foreground">
              {progress.label}
              {progress.total > 0 &&
                ` — ${progress.done.toLocaleString()} of ${progress.total.toLocaleString()}`}
            </p>
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={isApplying}>
            Cancel
          </Button>
          <Button
            onClick={(requireConfirmation || settingRemarketYes) ? () => setShowConfirmation(true) : handleApply}
            disabled={!hasChanges || isApplying || sameGroupError}
          >
            {isApplying ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Applying...
              </>
            ) : (
              'Apply Edit'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>

    <AlertDialog open={showConfirmation} onOpenChange={setShowConfirmation}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>{settingRemarketYes ? 'Confirm marketing permission' : 'Confirm Global Edit'}</AlertDialogTitle>
          <AlertDialogDescription asChild>
            <div className="space-y-2">
              <p>
                {settingRemarketYes
                  ? `I confirm I have received permission to send ${requireConfirmation ? `${effectiveCount.toLocaleString()} contacts` : 'these contacts'} marketing email. The affirmation is recorded per contact${requireConfirmation ? ', and this edit applies to all contacts matching your current view' : ''}.`
                  : `Are you sure you want to edit ${effectiveCount.toLocaleString()} contacts? This will update all contacts matching your current view.`}
              </p>
              {settingRemarketYes &&
                (resubscribeCandidates.length > 0 || (isAllMatchingMode && requireConfirmation)) && (
                  <p className="font-medium text-amber-600">
                    {resubscribeCandidates.length > 0
                      ? `${resubscribeCandidates.length} of these contact${resubscribeCandidates.length === 1 ? ' has' : 's have'} previously unsubscribed. `
                      : 'Some matching contacts may have previously unsubscribed. '}
                    Continuing confirms they requested to be re-subscribed — their unsubscribe will be cleared and a re-enable activity logged per contact.
                  </p>
                )}
            </div>
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>Cancel</AlertDialogCancel>
          <AlertDialogAction onClick={handleApply}>Yes, Apply</AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
    </>
  );
}
